import { useId } from 'react';
import {
  ComponentWithAs,
  HTMLChakraProps,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useBreakpointValue,
  usePrefersReducedMotion,
} from '@chakra-ui/react';
import { BREAKPOINTS } from '../../types/bitkit';
import Icon from '../Icon/Icon';
import Text from '../Text/Text';
import Tooltip from '../Tooltip/Tooltip';

export interface DialogProps
  extends Omit<HTMLChakraProps<'section'>, 'scrollBehavior'>,
    Pick<ModalProps, 'returnFocusOnClose'> {
  isClosable?: boolean;
  isOpen: boolean;
  onClose(): void;
  onCloseComplete?: () => void;
  size?: 'small' | 'medium' | 'large' | 'full';
  scrollBehavior?: 'inside' | 'outside';
  title: string;
  trapFocus?: boolean;
  variant?: 'default' | 'empty';
  closeNotice?: string;
}

const Dialog: ComponentWithAs<'section', DialogProps> = ({
  children,
  closeNotice,
  isClosable = true,
  isOpen,
  onClose,
  onCloseComplete,
  returnFocusOnClose = true,
  scrollBehavior = 'outside',
  size = 'medium',
  title,
  trapFocus = true,
  variant = 'default',
  ...rest
}: DialogProps) => {
  const dialogSize = useBreakpointValue({ [BREAKPOINTS.MOBILE]: 'mobile', [BREAKPOINTS.TABLET]: size });
  const prefersReducedMotion = usePrefersReducedMotion({ ssr: false });
  const headerId = useId();

  const closeBtn = (
    <ModalCloseButton isDisabled={!isClosable} size="large">
      <Icon name="Cross" />
    </ModalCloseButton>
  );

  return (
    <Modal
      closeOnEsc={isClosable}
      closeOnOverlayClick={false}
      isOpen={isOpen}
      motionPreset={prefersReducedMotion ? 'none' : 'scale'}
      onClose={isClosable ? onClose : () => {}}
      onCloseComplete={onCloseComplete}
      returnFocusOnClose={returnFocusOnClose}
      scrollBehavior={scrollBehavior}
      size={dialogSize}
      trapFocus={trapFocus}
    >
      <ModalOverlay />
      <ModalContent aria-labelledby={variant !== 'empty' ? headerId : undefined} {...rest}>
        {variant !== 'empty' && (
          <>
            <ModalHeader marginRight="48">
              <Text as="h1" id={headerId} textStyle="comp/dialog/title">
                {title}
              </Text>
            </ModalHeader>
            {closeNotice ? (
              <Tooltip label={closeNotice} placement="top">
                {closeBtn}
              </Tooltip>
            ) : (
              closeBtn
            )}
          </>
        )}
        {children}
      </ModalContent>
    </Modal>
  );
};

export default Dialog;
